/* ============================================================
   Single source of truth for every piece of copy, link and image
   on the page. Edit here — no component needs touching.

   Items marked TODO are placeholders: the brief did not supply
   the real value, so a sensible stand-in is used.
   ============================================================ */

export const business = {
  name: "Shree Siddhi Realty",
  shortName: "Shree Siddhi",
  role: "Real estate agent in Ahmedabad, Gujarat",
  rating: "5.0",
  reviewCount: 2,
  reviewLabel: "Google reviews",
  address: {
    lines: [
      "Westface, Shree Siddhi Realty, 407",
      "Zydus Hospital Rd, Thaltej",
      "Ahmedabad, Gujarat 380059",
    ],
    oneLine:
      "Westface, Shree Siddhi Realty, 407, Zydus Hospital Rd, Thaltej, Ahmedabad, Gujarat 380059",
  },
  /* A second office address appears on the previous site:
     B-504, One World Capital, Off Rajpath Rangoli Road, Bodakdev.
     Confirm which is current before launch — the map points at Thaltej. */
  tagline: "Turning dreams into reality",
  since: "2010",
  phoneDisplay: "+91 98986-88899",
  phoneDial: "+919898688899",
  phoneDisplayAlt: "+91 98254-40440",
  phoneDialAlt: "+919825440440",
  whatsapp: "919898688899",
  whatsappMessage:
    "Hello Shree Siddhi Realty, I would like to enquire about a property.",
  email: "shreesiddhirealty@gmail.com",
  website: "www.shreesiddhirealty.com", // TODO: replace with the live domain
  websiteUrl: "https://www.shreesiddhirealty.com", // TODO
  instagram: "https://www.instagram.com/shreesiddhirealty/",
  instagramHandle: "@shreesiddhirealty",
  hours: "Mon – Sat · 10:00 – 19:00",
};

const MAP_QUERY =
  "Shree Siddhi Realty, 407 Westface, Zydus Hospital Rd, Thaltej, Ahmedabad, Gujarat 380059";

export const mapEmbed =
  "https://www.google.com/maps?q=" + encodeURIComponent(MAP_QUERY) + "&output=embed";

export const mapDirections =
  "https://www.google.com/maps/dir/?api=1&destination=" +
  encodeURIComponent(MAP_QUERY);

export const whatsappLink =
  "https://wa.me/" +
  business.whatsapp +
  "?text=" +
  encodeURIComponent(business.whatsappMessage);

export const navLinks = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "projects", label: "Projects" },
  { id: "contact", label: "Contact" },
];

export const heroSlides = [
  {
    id: "commercial",
    kicker: "Commercial",
    title: ["Address", "that means", "business"],
    caption:
      "Grade-A offices, showrooms and retail frontage across the fastest-moving corridors of Ahmedabad.",
    image: "/images/hero-skyscrapers.jpg",
    meta: "Commercial",
  },
  {
    id: "residential",
    kicker: "High-End Residentials",
    title: ["Homes", "worth", "coming back to"],
    caption:
      "Curated apartments, penthouses and bungalows in the most sought-after addresses in the city.",
    image: "/images/project-green-towers.jpg",
    meta: "Residential",
  },
  {
    id: "land",
    kicker: "Agriculture & Non-Agriculture Land",
    title: ["Land", "that holds", "its value"],
    caption:
      "Title-clear agricultural and N.A. parcels, verified end to end before a rupee moves.",
    image: "/images/project-township.jpg",
    meta: "Land",
  },
  {
    id: "weekend",
    kicker: "Weekend Villas & Farms",
    title: ["A second", "life,", "an hour away"],
    caption:
      "Farmhouses, weekend villas and green retreats within easy reach of the city.",
    image: "/images/project-tower-dusk.jpg",
    meta: "Weekend",
  },
];

export const about = {
  eyebrow: "About us",
  headline: ["Your trusted", "partner", "in real estate."],
  body: [
    "We specialise across segments: high-end residential apartments, luxurious bungalows and serene weekend villas tailored to your lifestyle. Our services extend to plotting for those seeking custom-built homes, and to businesses through prime commercial spaces, corporate leasing and industrial leasing solutions.",
    "We blend innovation and excellence to provide properties that meet the highest standards of quality and sophistication — ensuring every client finds their perfect space.",
  ],
  stats: [
    { value: "12", suffix: "+", label: "Years of experience" },
    { value: "30", suffix: "+", label: "Industry partners" },
    { value: "500", suffix: "+", label: "Satisfied clients" },
  ],
  officeImage: "/images/office-exterior.jpg",
  officeCaption: "Our office — 407, Westface, Zydus Hospital Road, Thaltej",
  teamImage: null, // TODO: drop in the team photo, then set the path here
  teamCaption: "The team behind every mandate",
};

export const services = [
  {
    id: "commercial",
    index: "01",
    title: "Commercial",
    summary:
      "Exceptional commercial and corporate leasing — prime office space and retail outlets in strategic locations, with modern amenities and flexible lease terms that align with how your business actually runs.",
    points: [
      "Prime office space",
      "Retail outlets & showrooms",
      "Corporate leasing",
      "Industrial leasing",
    ],
    image: "/images/hero-skyscrapers.jpg",
  },
  {
    id: "residential",
    index: "02",
    title: "High-End Residentials",
    summary:
      "Luxurious 4 and 5 BHK apartments that blend elegance with comfort — state-of-the-art amenities, premium finishes and breathtaking views, backed by personalised service.",
    points: [
      "4 & 5 BHK apartments",
      "Luxurious bungalows",
      "Premium finishes",
      "Plotting for custom-built homes",
    ],
    image: "/images/project-residential.jpg",
  },
  {
    id: "land",
    index: "03",
    title: "Agriculture & Non-Agriculture Land",
    summary:
      "Expert help acquiring and managing agricultural and non-agricultural land. Fertile farmland or a development parcel — our market knowledge finds the right plot and verifies it properly.",
    points: [
      "Agricultural land parcels",
      "N.A. & development-ready plots",
      "Title & 7/12 verification",
      "Investment purchases",
    ],
    image: "/images/project-township.jpg",
  },
  {
    id: "weekend",
    index: "04",
    title: "Weekend Villas & Farms",
    summary:
      "An extensive range of weekend villas and farmhouses built for relaxation — serene locations, spacious layouts and modern amenities, an easy drive from the city.",
    points: [
      "Weekend villas",
      "Farmhouses & farm plots",
      "Serene gated locations",
      "Seamless purchase process",
    ],
    image: "/images/project-resort-campus.jpg",
  },
];

export const projects = [
  {
    id: "p1",
    name: "Skyline Commercial", // TODO: replace with the real project name
    category: "Commercial",
    location: "S.G. Highway, Ahmedabad",
    year: "2024",
    image: "/images/hero-skyscrapers.jpg",
  },
  {
    id: "p2",
    name: "Terrace Residences", // TODO
    category: "High-End Residential",
    location: "Thaltej, Ahmedabad",
    year: "2024",
    image: "/images/project-green-towers.jpg",
  },
  {
    id: "p3",
    name: "The Twin Towers", // TODO
    category: "High-End Residential",
    location: "Bopal, Ahmedabad",
    year: "2023",
    image: "/images/project-highrise-twins.jpg",
  },
  {
    id: "p4",
    name: "Courtyard Living", // TODO
    category: "High-End Residential",
    location: "Shela, Ahmedabad",
    year: "2023",
    image: "/images/project-residential.jpg",
  },
  {
    id: "p5",
    name: "Green Belt Township", // TODO
    category: "Land & Township",
    location: "Sanand, Ahmedabad",
    year: "2022",
    image: "/images/project-township.jpg",
  },
  {
    id: "p6",
    name: "The Weekend Estate", // TODO
    category: "Weekend Villas & Farms",
    location: "Sanathal, Ahmedabad",
    year: "2022",
    image: "/images/project-resort-campus.jpg",
  },
];

/* The real partner roster, as published on the previous site.
   `logo` stays null until a logo file lands in /public/images/clients/ —
   the tile shows the name set in the display serif until then. */
export const clientsHeading = {
  eyebrow: "Our valued partners",
  headline: "Serving with the industry leaders.",
  footnote: "…and many more",
};

export const clients = [
  { name: "Unispace", logo: null },
  { name: "HN SAFAL", logo: null },
  { name: "Shilp Group", logo: null },
  { name: "Shivalik Group", logo: null },
  { name: "Synthesis Spacelinks", logo: null },
  { name: "Aaryan Developers", logo: null },
  { name: "B | SAFAL", logo: null },
  { name: "Swati Procon", logo: null },
  { name: "Emberlynn Group", logo: null },
  { name: "A.Shridhar Group", logo: null },
  { name: "Rashmi Group", logo: null },
  { name: "Palak Shree Realty", logo: null },
  { name: "Savvy Group", logo: null },
  { name: "Ratnaakar Group", logo: null },
  { name: "Goyal & Co.", logo: null },
  { name: "True Value", logo: null },
  { name: "Sun Builders Group", logo: null },
  { name: "HR Group", logo: null },
  { name: "Rajshree Builders", logo: null },
  { name: "Sankalp Property", logo: null },
  { name: "Madhav Associate", logo: null },
].map((c) => ({ ...c, id: c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") }));

export const marqueeWords = [
  "Commercial",
  "High-End Residentials",
  "Agriculture & Non-Agriculture Land",
  "Weekend Villas & Farms",
];
